// Shareable daily-challenge results (Wordle mechanic): everyone plays the same
// seeded board, so a compact emoji card is directly comparable between friends.
// Streak state lives in localStorage — works for guests and signed-in players.

const STREAK_KEY = 'bg_daily_streak';
const SHARE_URL = 'https://braingrid.wolftechnologies.co.uk/play/daily';

// Daily #1 = 2026-07-06, the day Brain Grid launched. Dates are UTC — the
// backend seeds the puzzle from new Date().toISOString(), so we number off the
// same clock.
const DAILY_EPOCH_UTC = Date.UTC(2026, 6, 6);

export function getTodayDateString() {
  return new Date().toISOString().split('T')[0];
}

export function getDailyNumber(dateStr = getTodayDateString()) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const days = Math.floor((Date.UTC(y, m - 1, d) - DAILY_EPOCH_UTC) / 86400000);
  return Math.max(1, days + 1);
}

function readStreak() {
  try {
    const raw = JSON.parse(localStorage.getItem(STREAK_KEY) || 'null');
    if (raw && typeof raw.streak === 'number' && typeof raw.lastDate === 'string') return raw;
  } catch { /* corrupted — start over */ }
  return { streak: 0, lastDate: null };
}

export function getDailyStreak() {
  const { streak, lastDate } = readStreak();
  if (!lastDate) return 0;
  // A streak is only "alive" if the last play was today or yesterday.
  const today = getTodayDateString();
  return (lastDate === today || lastDate === previousDateString(today)) ? streak : 0;
}

function previousDateString(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d) - 86400000).toISOString().split('T')[0];
}

// Called once per finished daily game. Idempotent within a day.
export function updateDailyStreak(dateStr = getTodayDateString()) {
  const current = readStreak();
  if (current.lastDate === dateStr) return current.streak;
  const streak = current.lastDate === previousDateString(dateStr) ? current.streak + 1 : 1;
  try { localStorage.setItem(STREAK_KEY, JSON.stringify({ streak, lastDate: dateStr })); } catch { /* ignore */ }
  return streak;
}

// 10-block score meter, one block per 500 pts (5,000 = full bar). Spoiler-free
// and comparable at a glance, like Wordle's guess grid.
function scoreMeter(score) {
  const blocks = Math.min(20, score / 250); // half-blocks for the 🟨 partial
  const full = Math.floor(blocks / 2);
  const half = blocks % 2 >= 1 ? 1 : 0;
  return '🟩'.repeat(full) + '🟨'.repeat(half) + '⬜'.repeat(Math.max(0, 10 - full - half));
}

export function buildDailyShareText({ score, linesCleared, bestCombo, streak, dateStr }) {
  const lines = [
    `🧠 Brain Grid Daily #${getDailyNumber(dateStr)}`,
    `⭐ ${score.toLocaleString()} pts · ${linesCleared} lines${bestCombo > 1 ? ` · x${bestCombo} combo` : ''}`,
    scoreMeter(score),
  ];
  if (streak >= 2) lines.push(`🔥 ${streak}-day streak`);
  lines.push(SHARE_URL);
  return lines.join('\n');
}

// Returns 'shared' | 'copied' | 'failed' so the caller can toast accordingly.
export async function shareDailyResult(text) {
  if (navigator.share) {
    try {
      await navigator.share({ text });
      return 'shared';
    } catch (err) {
      if (err?.name === 'AbortError') return 'shared'; // user closed the sheet — not a failure
      // fall through to clipboard
    }
  }
  try {
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch {
    return 'failed';
  }
}
